<template>
  <div class="expandable-card">
    <template v-if="isLoading">
      <div class="loading"><loader-small /></div>
    </template>
    <template v-else>
      <div class="header">
        <slot name="header" />
      </div>

      <div class="details">
        <slot name="details" />

        <div class="expandable-wrapper">
          <div
            class="badge badge-xs badge-muted"
            @click="toggleCard()">
            {{ expandedLabel }}
          </div>
        </div>
      </div>

      <transition name="squish">
        <div
          v-if="isExpanded"
          class="expandable">
          <slot name="expanded-content" />
        </div>
      </transition>
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { capitalize, computed, ref } from 'vue';

  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import i18n from '@/vueI18n';

  interface IProps {
    isLoading?: boolean;
  }

  withDefaults(defineProps<IProps>(), {
    isLoading: false,
  });

  const isExpanded = ref<boolean>(false);

  const expandedLabel = computed<string>(() => {
    const key = isExpanded.value ? 'actions.collapse' : 'actions.expand';
    return capitalize(i18n.global.t(key));
  });

  function toggleCard(value: boolean = null): void {
    isExpanded.value = value ?? !isExpanded.value;
  }

  defineExpose({
    isExpanded,
    toggleCard,
  });
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .expandable-card {
    border: solid 0.1rem var(--secondaryColorDesaturated50Lighten30);
    border-radius: 3px;
    display: block;
    position: relative;

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    p {
      margin: 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .header {
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--paneHeaderBackgroundColor);
      padding: 1rem;
    }

    .details {
      margin-top: 0.5rem;

      .expandable-wrapper {
        position: absolute;
        bottom: -1rem;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;

        .badge {
          cursor: pointer;
          transition: all 0.3s;

          &:hover {
            background-color: var(--secondaryColorDesaturated50Lighten30);
            color: var(--secondaryColorLight);
            transition: all 0.3s;
          }
        }
      }
    }

    .expandable {
      margin-bottom: 2rem;
      animation: expand 0.5s ease;
    }
  }
</style>
